import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useRestTimerStore } from "../store"
import { Button } from "./ui/button"

const PRESETS = [60, 90, 120, 180]

let audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx || audioCtx.state === "closed") {
      audioCtx = new AudioContext()
    }
    if (audioCtx.state === "suspended") {
      audioCtx.resume()
    }
    return audioCtx
  } catch {
    return null
  }
}

function playDing() {
  const ctx = getAudioContext()
  if (!ctx) {
    return
  }

  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.type = "sine"
  osc.frequency.value = 880
  gain.gain.value = 0.3
  osc.start()
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5)
  osc.stop(ctx.currentTime + 0.5)
}

export default function RestTimer() {
  const { remaining, total, isRunning, isPaused, showPresets, start, tick, pause, resume, dismiss } =
    useRestTimerStore()
  const [customValue, setCustomValue] = useState("")
  const [justFinished, setJustFinished] = useState(false)
  const prevRunning = useRef(isRunning)

  useEffect(() => {
    if (!isRunning || isPaused) {
      return
    }
    const id = window.setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [isRunning, isPaused, tick])

  useEffect(() => {
    if (prevRunning.current && !isRunning && remaining === 0 && total > 0) {
      playDing()
      setJustFinished(true)
      const timeout = window.setTimeout(() => {
        setJustFinished(false)
        dismiss()
      }, 5000)
      return () => clearTimeout(timeout)
    }
    prevRunning.current = isRunning
  }, [isRunning, remaining, total, dismiss])

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${m}:${sec.toString().padStart(2, "0")}`
  }

  const progress = total > 0 ? ((total - remaining) / total) * 100 : 0

  if (!isRunning && !showPresets && !justFinished) {
    return null
  }

  if (showPresets && !isRunning) {
    return (
      <div className="fixed right-0 bottom-0 left-0 z-50 border-t bg-background p-4 shadow-lg">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-3">
          <div className="flex w-full items-center justify-between">
            <span className="font-medium text-sm">Rest timer</span>
            <button onClick={dismiss} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-2">
            {PRESETS.map((s) => (
              <Button key={s} variant="outline" size="sm" onClick={() => start(s)}>
                {s >= 60 ? `${s / 60}m` : `${s}s`}
              </Button>
            ))}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1}
                max={600}
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="s"
                className="workout-input w-16 text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={!customValue || Number(customValue) <= 0}
                onClick={() => {
                  const val = Number(customValue)
                  if (val > 0) {
                    start(val)
                    setCustomValue("")
                  }
                }}
              >
                Go
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`fixed right-0 bottom-0 left-0 z-50 cursor-pointer border-t bg-background shadow-lg transition-colors ${
        justFinished ? "animate-pulse border-green-500" : ""
      }`}
      onClick={() => {
        if (justFinished) {
          return
        }
        isPaused ? resume() : pause()
      }}
    >
      <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
        <span className="font-medium text-sm">{justFinished ? "Rest complete!" : isPaused ? "Paused" : "Resting"}</span>
        <span className="font-mono text-xl">{formatTime(remaining)}</span>
        <button
          onClick={(e) => {
            e.stopPropagation()
            dismiss()
          }}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>
      <div className="h-1 w-full bg-muted">
        <div
          className={`h-full transition-all duration-1000 ${justFinished ? "bg-green-500" : "bg-primary"}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  )
}
